import React, { forwardRef, useCallback, useContext, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Button, notification } from 'antd';

import { CaretDown, Cube, SunHorizon } from '@phosphor-icons/react';

import { useOneButtonStore } from '../../../../store/oneButtonStore';

import { useDashboardV3Data } from './sections/dashboardV3/useDashboardV3Data';

import type { V3ProductCardModel } from './sections/dashboardV3/useDashboardV3Data';

import { SystemDesignProductCard } from './SystemDesignProductCard';

import DashboardModal from './DashboardModal';

import AddersModal, { type AddersModalHandle } from './AddersModal';

import ProductsFinanceHubSection from './ProductsFinanceHubSection';
import { ProductsFinanceHubContext, ProductsFinanceHubProvider } from './ProductsFinanceHubContext';

import { resolveCommerceHubModalZ } from './oneButtonOverlayZ';

import quoteServices, { type DealOpportunityProductAdder } from '../../../../services/oneButtonServices/quoteServices';
import { useSunnyFocusedSectionOpen } from './useSunnyFocusedSectionOpen';
import { useSolarSectionCalculationMessage } from '../../../../hooks/calculationStateHook';
import {
    SystemDesignMissingBanner,
} from './SystemDesignMissingBanner';
import type { SystemDesignMissingItem, SystemDesignMissingItemId } from './systemDesignMissingItems';
import { isDashboardCustomerSectionReady } from './systemDesignMissingItems';

import './oneButton-css/ProductionsCardMergedSystemSection.css';

export type ProductionsCardMergedSystemSectionHandle = {
    ensureOpen: () => void;
};



function getAvailableAdderProductTypesFromOpportunity(

    opportunityDetails: any | null | undefined,

    productType: string | null | undefined,

): string[] {

    const productTypes: string[] = [];

    if (productType === 'RR') return ['R&R'];



    const interest = opportunityDetails?.OpportunityInterest ?? opportunityDetails?.opportunityInterest ?? {};

    if (interest?.RRR) productTypes.push('RRR');

    if (interest?.Solar) productTypes.push('Solar');

    if (interest?.Battery) productTypes.push('Battery');

    if (interest?.Roof) productTypes.push('Roof');

    return productTypes;

}



export type ProductionsCardMergedSystemSectionProps = {

    loading?: boolean;

    disabled?: boolean;

    sectionHeadActions: React.ReactNode;

    solarCardHeadActions?: React.ReactNode;

    showRoofSelectProductMessage: boolean;

    showBatterySelectProductMessage: boolean;

    roofTitle?: string;

    displayShingleName: string | null;

    shingleSwatch?: string;

    onDesignSolar: () => void;

    onDesignRoof: () => void;

    /** Roof card Design button content when Hover is gated (logo + "Hover Roof Design"). */
    roofDesignLabel?: React.ReactNode;

    onDesignBattery: () => void;

    onUtility: () => void;

    onOpenProducts: () => void;

    onRoofDetails: () => void;

    onSolarPricingClick?: () => void;

    /** Dedicated System rail panel — always show product cards (no collapse). */
    alwaysExpanded?: boolean;

    missingItems?: SystemDesignMissingItem[];

    onFixMissingItem?: (id: SystemDesignMissingItemId) => void;

    onHoverJobClick?: () => void;

    hoverJobAvailable?: boolean;

    hoverJobBusy?: boolean;

};



export const ProductionsCardMergedSystemSection = forwardRef<
    ProductionsCardMergedSystemSectionHandle,
    ProductionsCardMergedSystemSectionProps
>(({

    loading = false,

    disabled = false,

    sectionHeadActions,

    solarCardHeadActions,

    showRoofSelectProductMessage,

    showBatterySelectProductMessage,

    roofTitle,

    displayShingleName,

    shingleSwatch,

    onDesignSolar,

    onDesignRoof,

    roofDesignLabel = 'Design',

    onDesignBattery,

    onUtility,

    onOpenProducts,

    onRoofDetails,

    onSolarPricingClick,

    alwaysExpanded = false,

    missingItems = [],

    onFixMissingItem,

    onHoverJobClick,

    hoverJobAvailable = false,

    hoverJobBusy = false,

}, ref) => {

    const theme = useOneButtonStore((s) => s.theme);

    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);

    const productType = useOneButtonStore((s) => s.productType);

    const systemDesignQuotesLoading = useOneButtonStore((s) => s.systemDesignQuotesLoading);

    const quotesOpen = useOneButtonStore((s) => s.commerceHubQuotesOpen);

    const customerSectionReady = isDashboardCustomerSectionReady(missingItems);

    const [productsOpen, setProductsOpen] = useState(false);
    const productsHub = useContext(ProductsFinanceHubContext);

    const closeProducts = () => {
        productsHub?.resetHubRowsFromSavedOpportunity();
        setProductsOpen(false);
    };

    const modalClass = `obd-sync-modal obd-commerce-hub-modal obd-theme-${theme}`;

    const modalRoot = `obd-sync-modal-root obd-commerce-hub-modal-root obd-theme-${theme}`;

    const productsStackedAboveQuotes = quotesOpen && productsOpen;

    const salesForceId =
        opportunityDetails?.SalesForce_Id ??
        (typeof window !== 'undefined' ? sessionStorage.getItem('SalesForceId') : null) ??
        undefined;



    const openProducts = () => {
        productsHub?.resetHubRowsFromSavedOpportunity();
        setProductsOpen(true);
        onOpenProducts();
    };



    const [opportunityProductAdders, setOpportunityProductAdders] = useState<DealOpportunityProductAdder[]>([]);
    const [dealAddersRefreshKey, setDealAddersRefreshKey] = useState(0);

    const { products } = useDashboardV3Data({
        finalizedDesignMetricsOnly: true,
        dealAddersRefreshKey,
    });

    const solarCalcLoadingMessage = useSolarSectionCalculationMessage();

    const coreProducts = useMemo((): V3ProductCardModel[] => {
        return products
            .filter((p) => p.kind !== 'gutter')
            .map((p) => {
                if (p.kind === 'roof') {
                    return {
                        ...p,
                        title: roofTitle ?? p.title,
                        shingleLabel: displayShingleName ?? p.shingleLabel,
                        shingleSwatch: p.shingleSwatch,
                    };
                }
                return p;
            });
    }, [products, roofTitle, displayShingleName, shingleSwatch]);

    const cardClassName = `obd-card obd-card--system-design obd-dashboard-main-card obd-theme-${theme}${disabled ? ' obd-disabled-section' : ''}${alwaysExpanded ? ' obd-card--system-design--pinned-open' : ''}`;

    const [sectionOpen, setSectionOpen] = useSunnyFocusedSectionOpen('system-design', {
        alwaysOpen: alwaysExpanded,
    });

    useImperativeHandle(ref, () => ({
        ensureOpen: () => setSectionOpen(true),
    }), [setSectionOpen]);
    const [addersOpen, setAddersOpen] = useState(false);
    const [addersLoading, setAddersLoading] = useState(false);
    const [addersSaving, setAddersSaving] = useState(false);
    const addersModalRef = useRef<AddersModalHandle | null>(null);

    const [noticeApi, noticeContextHolder] = notification.useNotification({ maxCount: 3 });

    const notifClass = `obd-notification obd-theme-${theme}`;



    const opportunityIdForQuoteModal = useMemo(() => {

        const od = opportunityDetails as { Opportunity_Id?: string | number; OpportunityId?: string | number } | null | undefined;

        return od?.Opportunity_Id ?? od?.OpportunityId ?? null;

    }, [opportunityDetails]);



    const availableAdderProductTypes = useMemo(

        () => getAvailableAdderProductTypesFromOpportunity(opportunityDetails, productType),

        [opportunityDetails, productType],

    );

    const { setProductTypeAdders } = useOneButtonStore();

    const loadDealAdders = useCallback(async () => {
        const oid = opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId;
        if (oid == null || String(oid).trim() === '') {
            setOpportunityProductAdders([]);
            return;
        }
        try {
            const rows = await quoteServices.DealOpportunityProductAdders(String(oid));
            setOpportunityProductAdders(Array.isArray(rows) ? rows : []);
            setDealAddersRefreshKey((k) => k + 1);
        } catch {
            setOpportunityProductAdders([]);
        }
    }, [opportunityDetails]);

    const openAddersModal = useCallback(async () => {
        const oid = opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId;
        if (oid == null || String(oid).trim() === '') {
            noticeApi.warning({
                message: 'Missing opportunity',
                description: 'Unable to load deal adders without an opportunity id.',
                className: notifClass,
            });
            return;
        }
        setAddersOpen(true);
        setAddersLoading(true);
        try {
            const rows = await quoteServices.DealOpportunityProductAdders(String(oid));
            setOpportunityProductAdders(Array.isArray(rows) ? rows : []);
            try {
                const adders = await quoteServices.getProductTypesAdders();
                setProductTypeAdders(Array.isArray(adders) ? adders : []);
            } catch {
                setProductTypeAdders([]);
            }
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Request failed.';
            setOpportunityProductAdders([]);
            noticeApi.error({
                message: 'Could not load deal adders',
                description: msg,
                className: notifClass,
            });
        } finally {
            setAddersLoading(false);
        }
    }, [opportunityDetails, noticeApi, notifClass, setProductTypeAdders]);



    const handleSaveAdders = useCallback(async () => {

        if (!addersModalRef.current) return;

        setAddersSaving(true);

        try {

            const response = await addersModalRef.current.saveAdders();

            const responseData = response as { IsSuccessful?: boolean; Message?: string; message?: string } | undefined;

            if (responseData?.IsSuccessful === false) {

                throw new Error(String(responseData.Message ?? responseData.message ?? 'Unable to save adders.'));

            }

            noticeApi.success({

                message: 'Adders saved successfully',

                className: notifClass,

            });

            setAddersOpen(false);
            await loadDealAdders();

        } catch (e: unknown) {

            const msg =

                String((e as any)?.response?.data?.Message ?? (e as any)?.response?.data?.message ?? '').trim() ||

                (e instanceof Error ? e.message : 'Request failed.');

            noticeApi.error({

                message: 'Could not save adders',

                description: msg,

                className: notifClass,

            });

        } finally {

            setAddersSaving(false);

        }

    }, [loadDealAdders, noticeApi, notifClass]);


    const collapseHeader = (
        <div
            role={alwaysExpanded ? undefined : 'button'}
            tabIndex={alwaysExpanded ? undefined : 0}
            className={`obd-quotes__header obd-system-design__collapse-header${alwaysExpanded ? ' obd-system-design__collapse-header--pinned' : ''}`}
            aria-expanded={sectionOpen}
            onClick={alwaysExpanded ? undefined : () => setSectionOpen((open) => !open)}
            onKeyDown={
                alwaysExpanded
                    ? undefined
                    : (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSectionOpen((open) => !open);
                          }
                      }
            }
        >
            <span className="obd-card__label obd-card__label--section-icon">
                <span className="obd-section-head-icon obd-section-head-icon--inline" aria-hidden>
                    <SunHorizon size={16} weight="duotone" />
                </span>
                System &amp; Design
            </span>
            <div
                className="obd-system-design__head-end obd-financing__header-actions"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
            >
                {systemDesignQuotesLoading ? (
                    <span className="obd-system-design__quotes-loading" role="status" aria-live="polite">
                        Loading quotes &amp; agreements…
                    </span>
                ) : null}
                {solarCalcLoadingMessage ? (
                    <span className="obd-system-design__calc-banner" role="status" aria-live="polite">
                        {solarCalcLoadingMessage}
                    </span>
                ) : null}
                {sectionHeadActions}
                {alwaysExpanded ? null : (
                    <CaretDown
                        size={14}
                        weight="bold"
                        className={`obd-quotes__chevron${sectionOpen ? ' obd-quotes__chevron--open' : ''}`}
                        aria-hidden
                    />
                )}
            </div>
        </div>
    );



    const systemDesignCard = customerSectionReady ? (
            <div className={cardClassName}>
                {noticeContextHolder}
                {collapseHeader}
                {sectionOpen ? (
                    <div className="obd-dashboard-main-card__body">
                        <div className="obd-system-design__grid">

                    {coreProducts.map((model) => {

                        const showFinanceProductRow =

                            (model.kind === 'roof' && showRoofSelectProductMessage) ||

                            (model.kind === 'battery' && showBatterySelectProductMessage);

                        const financeProductName =

                            model.kind === 'roof'

                                ? ((opportunityDetails?.OpportunityProduct ?? []).some(
                                      (p: any) =>
                                          String(p?.Product_Type_Name ?? '').toLowerCase() === 'rrr',
                                  ) &&
                                  !(opportunityDetails?.OpportunityProduct ?? []).some(
                                      (p: any) =>
                                          String(p?.Product_Type_Name ?? '').toLowerCase() === 'roof',
                                  )
                                      ? 'RRR'
                                      : 'Roof')

                                : model.kind === 'battery'

                                  ? 'Battery'

                                  : undefined;



                        return (

                            <SystemDesignProductCard

                                key={model.kind}

                                model={model}

                                showFinanceProductRow={showFinanceProductRow}

                                financeProductName={financeProductName}

                                onDesign={

                                    model.kind === 'solar'

                                        ? onDesignSolar

                                        : model.kind === 'roof'

                                          ? onDesignRoof

                                          : onDesignBattery

                                }

                                designLabel={model.kind === 'roof' ? roofDesignLabel : 'Design'}

                                onUtility={(model.kind === 'solar' || model.kind === 'battery') ? onUtility : undefined}

                                onAdders={openAddersModal}

                                onOpenProducts={openProducts}

                                onWastageDecrease={model.kind === 'roof' ? onRoofDetails : undefined}

                                onWastageIncrease={model.kind === 'roof' ? onRoofDetails : undefined}

                                onShingleClick={model.kind === 'roof' ? onRoofDetails : undefined}

                                headEndActions={model.kind === 'solar' ? solarCardHeadActions : undefined}

                                onHoverJobClick={model.kind === 'roof' ? onHoverJobClick : undefined}

                                hoverJobAvailable={model.kind === 'roof' ? hoverJobAvailable : undefined}

                                hoverJobBusy={model.kind === 'roof' ? hoverJobBusy : undefined}

                            />

                        );

                    })}

                </div>

            </div>
            ) : null}

            <DashboardModal
                title="Add Adders"
                open={addersOpen}
                onClose={() => setAddersOpen(false)}
                footer={
                    <>
                        <Button onClick={() => setAddersOpen(false)} disabled={addersSaving}>
                            Cancel
                        </Button>
                        <Button type="primary" onClick={handleSaveAdders} loading={addersSaving}>
                            Save
                        </Button>
                    </>
                }
                width="80vw"
                theme={theme}
                centered
                maskClosable={false}
                destroyOnClose
                rootClassName={`obd-sync-modal-root obd-form-controls-modal-root obd-contact-modal obd-adders-dashboard-modal obd-theme-${theme}`}
                zIndex={10070}
                loading={addersLoading}
                loadingMessage="Loading adders..."
            >
                <AddersModal
                    ref={addersModalRef}
                    productTypes={availableAdderProductTypes}
                    theme={theme}
                    opportunityId={opportunityIdForQuoteModal}
                    opportunityDetails={opportunityDetails}
                    opportunityProductAdders={opportunityProductAdders}
                />
            </DashboardModal>


            <DashboardModal
                open={productsOpen}
                onClose={closeProducts}
                maskClosable={false}
                footer={null}
                title={
                    <>
                        <Cube weight="duotone" aria-hidden />
                        <span>Products</span>
                    </>
                }
                width="min(96vw, 920px)"
                centered
                destroyOnClose={false}
                zIndex={resolveCommerceHubModalZ(false, 0, productsStackedAboveQuotes)}
                theme={theme}
                className={modalClass}
                rootClassName={modalRoot}
                loading={!!loading}
            >
                <div className={`obd-commerce-hub-modal__shell obd-theme-${theme}`}>
                    <div className="obd-sync-modal__content-body obd-commerce-hub-modal__body obd-commerce-hub-modal__body--products">
                        <ProductsFinanceHubSection
                            salesForceId={salesForceId}
                            loading={loading}
                            onSolarPricingClick={onSolarPricingClick}
                            embeddedInModal
                        />
                    </div>
                </div>
            </DashboardModal>
        </div>
    ) : null;

    return (
        <>
            {missingItems.length > 0 && onFixMissingItem ? (
                <div className="obd-system-design-missing-slot">
                    <SystemDesignMissingBanner items={missingItems} onFixItem={onFixMissingItem} />
                </div>
            ) : null}
            {productsHub ? systemDesignCard : (
                <ProductsFinanceHubProvider salesForceId={salesForceId}>
                    {systemDesignCard}
                </ProductsFinanceHubProvider>
            )}
        </>
    );

});

ProductionsCardMergedSystemSection.displayName = 'ProductionsCardMergedSystemSection';

